<template>
    <div
        class="box box-primary"
        :class="{ 'is-loading': isChartsLoading }"
    >
        <div class="box-header with-border">
            <h3 class="box-title">{{ $t('charts') }}</h3>

            <img
                v-show="isChartsLoading"
                class="table-row-preloader ml10"
                rel="preload"
                src="/img/table-row-preloader.gif"
            />

            <div class="box-tools pull-right">
                <button
                    class="btn btn-box-tool"
                    :title="$t('collapse')"
                    :disabled="!isReport"
                    type="button"
                    @click="callReportByDates()"
                >
                    <i
                        class="fa"
                        :class="{
                            'fa-plus': isCollapse,
                            'fa-minus': !isCollapse,
                        }"
                    />
                </button>
            </div>
        </div>

        <template v-if="!isChartsLoading">
            <vue-slide-up-down
                :active="!isCollapse"
            >
                <div v-if="isReport">
                    <div class="box-body">
                        <div class="row mb10 pb10">
                            <div class="col-xs-12">
                                <button
                                    v-for="(entity, key) in chartEntities"
                                    :key="key"
                                    class="btn btn-default charts-btn"
                                    :class="{
                                        'is-active': activeChartKey === key,
                                    }"
                                    type="button"
                                    :style="{
                                        backgroundColor: activeChartKey === key ? entity.color : undefined,
                                        borderColor: activeChartKey === key ? entity.borderColor : undefined,
                                    }"
                                    @click="activeChartKey = key"
                                >
                                    {{ key }}
                                </button>
                            </div>
                        </div>

                        <div class="row">
                            <div
                                class="col-xs-12"
                                :class="{
                                    'col-md-4': isChartDoughnut,
                                    'col-md-6': !isChartDoughnut,
                                }"
                            >
                                <LineChart
                                    v-if="isChartLineData"
                                    class="charts-block"
                                    :chartData="chartLineData"
                                    :chartOptions="chartOptionsLine"
                                />
                                <div
                                    v-else
                                    class="charts-block-empty"
                                >
                                    No data!
                                </div>
                            </div>

                            <div
                                class="col-xs-12"
                                :class="{
                                    'col-md-4': isChartDoughnut,
                                    'col-md-6': !isChartDoughnut,
                                }"
                            >
                                <BarChart
                                    v-if="isChartBarData"
                                    class="charts-block"
                                    :chartData="chartBarData"
                                    :chartOptions="chartOptionsBar"
                                />
                                <div
                                    v-else
                                    class="charts-block-empty"
                                >
                                    No data!
                                </div>
                            </div>

                            <div
                                v-if="isChartDoughnut"
                                class="col-xs-12 col-md-4"
                            >
                                <DoughnutChart
                                    v-if="isChartDoughnutData"
                                    class="charts-block"
                                    :chartData="chartDoughnutData"
                                    :chartOptions="chartOptionsDoughnut"
                                />
                                <div
                                    v-else
                                    class="charts-block-empty"
                                >
                                    No data!
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-else
                    class="box-body clearfix"
                >
                    <p>{{ $t('messages.report.noReport') }}</p>
                </div>
            </vue-slide-up-down>
        </template>
    </div>
</template>

<script>
import { Line as LineChart, Bar as BarChart, Doughnut as DoughnutChart } from 'vue-chartjs/legacy';
import { Chart as ChartJS, Title, Tooltip, BarElement, CategoryScale, PointElement, LineElement, LinearScale, ArcElement } from 'chart.js';
import formatsMixin from '@/mixins/formatsMixin';

ChartJS.register(Title, Tooltip, BarElement, CategoryScale, PointElement, LineElement, LinearScale, ArcElement);

ChartJS.register({
    id: 'ElementsCenter',
    beforeDraw: (chart) => {
        if (chart.config.options.elements && chart.config.options.elements.center) {
            const centerConfig = chart.config.options.elements.center;
            const width = chart.width;
            const height = chart.height;
            const ctx = chart.ctx;

            ctx.restore();

            ctx.font = '700 1.5em sans-serif';
            ctx.textBaseline = 'middle';

            const color = '#1a1a1a';
            const text = centerConfig.text;
            const textX = Math.round((width - ctx.measureText(text).width) / 2);
            const textY = height / 2;

            ctx.fillStyle = color;
            ctx.fillText(text, textX, textY);
            ctx.save();
        }
    },
});

export default {
    name: 'DashboardCharts',
    components: {
        LineChart,
        BarChart,
        DoughnutChart,
    },
    mixins: [formatsMixin],
    props: {
        isChartsLoading: {
            type: Boolean,
            default: false,
        },
        report: {
            type: Object,
            default: () => ({}),
        },
        getReportByDates: {
            type: Function,
            required: true,
        },
    },
    data() {
        return {
            isCollapse: true,
            activeChartKey: 'Profit $',
            chartEntities: {
                'Profit $': {
                    key: 'Profit $',
                    color: '#3c8dbc',
                    borderColor: '#1cabfd',
                    alias: 'profit_amount_number',
                },
                'pCPM $': {
                    key: 'pCPM $',
                    color: '#00a65a',
                    borderColor: '#14dd83',
                    alias: 'pcpm_number',
                },
                'Clicks %': {
                    key: 'Clicks %',
                    color: '#F07F03',
                    borderColor: '#ff9e36',
                    alias: 'clicks_percent_number',
                },
            },
        };
    },
    computed: {
        isReport() {
            return !!(this.report && Object.keys(this.report).length && (this.report.items || []).length);
        },
        isChartLineData() {
            return !!Object.keys(this.report.totalsByDates || {}).length;
        },
        isChartBarData() {
            return !!(
                this.chartBarData &&
                this.chartBarData.datasets &&
                this.chartBarData.datasets[0].data &&
                this.chartBarData.datasets[0].data.length &&
                this.chartBarData.datasets[0].data.reduce((a = 0, e) => a + e) > 0
            );
        },
        isChartDoughnut() {
            return !!this.chartDoughnutData;
        },
        isChartDoughnutData() {
            return !!(
                this.chartDoughnutData &&
                this.chartDoughnutData.datasets &&
                this.chartDoughnutData.datasets[0].data &&
                this.chartDoughnutData.datasets[0].data.length &&
                this.chartDoughnutData.datasets[0].data.reduce((a = 0, e) => a + e) > 0
            );
        },
        reportItemsEntities() {
            return (this.report.items || []).filter((item) => item.entity);
        },
        reportItemsBackgroundColor() {
            return this.reportItemsEntities.map(this.getRandColor);
        },
        chartEntity() {
            return this.chartEntities[this.activeChartKey] || {};
        },
        chartLineData() {
            const totalsByDates = this.report.totalsByDates || {};

            return {
                labels: Object.keys(totalsByDates).map((total) => {
                    const date = this.formatDate(total, { timeStyle: undefined }).split('.');
                    return `${date[0]}.${date[1]}`;
                }),
                datasets: [
                    {
                        label: this.activeChartKey,
                        data: Object.values(totalsByDates).map((total) => {
                            const entityTotal = total[this.chartEntity.alias];
                            return entityTotal ? entityTotal : 0;
                        }),
                        backgroundColor: this.chartEntity.color,
                        borderColor: this.chartEntity.borderColor,
                        pointStyle: 'circle',
                        pointRadius: 5,
                        pointHoverRadius: 8,
                        fill: 1,
                    },
                ],
            };
        },
        chartOptionsLine() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    axis: 'x',
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                },
            };
        },
        chartBarData() {
            const items = this.reportItemsEntities.filter((item) => item[this.chartEntity.alias] !== 0);
            const labels = items.map((item) => item.entity.name);
            const data = items.map((item) => item[this.chartEntity.alias]);

            return {
                labels,
                datasets: [
                    {
                        label: this.activeChartKey,
                        data,
                        backgroundColor: this.chartEntity.color,
                        borderColor: this.chartEntity.borderColor,
                    },
                ],
            };
        },
        chartOptionsBar() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    intersect: false,
                    axis: 'x',
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                },
            };
        },
        chartDoughnutData() {
            if (this.chartEntity.key !== 'Profit $') {
                return null;
            }

            const items = this.reportItemsEntities.filter((item) => item[this.chartEntity.alias] > 0);
            const labels = items.map((item) => item.entity.name);
            const data = items.map((item) => item[this.chartEntity.alias]);

            return {
                labels,
                datasets: [
                    {
                        label: this.activeChartKey,
                        data,
                        backgroundColor: this.reportItemsBackgroundColor,
                    },
                ],
            };
        },
        chartOptionsDoughnut() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                },
                elements: {
                    center: {
                        text: this.activeChartKey,
                    },
                },
            };
        },
    },
    watch: {
        report() {
            this.activeChartKey = Object.keys(this.chartEntities)[0];
        },
    },
    methods: {
        getRandColor() {
            let n = (Math.random() * 0xfffff * 1000000).toString(16);
            return '#' + n.slice(0, 6);
        },
        callReportByDates() {
            this.isCollapse = !this.isCollapse
            if (!this.isCollapse) {
                this.getReportByDates();
            }
        },
        collapseChart() {
            this.isCollapse = true;
        },
    },
};
</script>

<style lang="css" src="./DashboardCharts.css" />
